import { useState, useEffect } from 'react'
import { Search, User, ExternalLink } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Modal } from '@/components/ui/modal'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import {
  useIdLookup,
  getIdTypesForBody,
  autoDetectGoverningBody,
  type GoverningBody,
  type IdType,
  type IdLookupFilters,
} from '../hooks/use-id-lookup'

interface IdLookupModalProps {
  open: boolean
  onClose: () => void
}

const governingBodies: { value: GoverningBody; label: string }[] = [
  { value: 'ALL', label: 'All' },
  { value: 'NRA', label: 'NRA' },
  { value: 'CPSA', label: 'CPSA' },
  { value: 'NSRA', label: 'NSRA' },
  { value: 'UKPSA', label: 'UKPSA' },
  { value: 'MLAGB', label: 'MLAGB' },
]

const bodyBadgeVariant: Record<GoverningBody, 'default' | 'info' | 'success' | 'warning'> = {
  NRA: 'info',
  CPSA: 'success',
  NSRA: 'default',
  UKPSA: 'warning',
  MLAGB: 'default',
  ALL: 'default',
}

export function IdLookupModal({ open, onClose }: IdLookupModalProps) {
  const navigate = useNavigate()
  const { results, loading, error, search, clearResults } = useIdLookup()

  const [governingBody, setGoverningBody] = useState<GoverningBody>('ALL')
  const [idType, setIdType] = useState<IdType>('all')
  const [idNumber, setIdNumber] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [hasSearched, setHasSearched] = useState(false)

  const idTypes = getIdTypesForBody(governingBody)

  // Reset idType when governing body changes
  useEffect(() => {
    const types = getIdTypesForBody(governingBody)
    if (types.length > 0) {
      setIdType(types[0]!.value)
    }
  }, [governingBody])

  // Auto-detect governing body from ID number
  useEffect(() => {
    if (idNumber.length >= 4) {
      const detected = autoDetectGoverningBody(idNumber)
      if (detected) {
        setGoverningBody(detected)
      }
    }
  }, [idNumber])

  const handleSearch = async () => {
    const filters: IdLookupFilters = {
      governingBody,
      idType,
      idNumber,
      firstName,
      lastName,
    }

    // Require at least one search term
    if (!idNumber.trim() && !firstName.trim() && !lastName.trim()) return

    setHasSearched(true)
    await search(filters)
  }

  const handleClear = () => {
    setGoverningBody('ALL')
    setIdType('all')
    setIdNumber('')
    setFirstName('')
    setLastName('')
    setHasSearched(false)
    clearResults()
  }

  const handleViewProfile = (profileId: string) => {
    onClose()
    navigate(`/profile?view=${profileId}`)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Lookup Shooter" size="lg">
      <div className="space-y-5">
        {/* Search form */}
        <div className="space-y-4">
          {/* Governing body and ID type */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="lookup-body" className="mb-1 block text-sm font-medium text-gray-700">
                Governing Body
              </label>
              <select
                id="lookup-body"
                value={governingBody}
                onChange={(e) => setGoverningBody(e.target.value as GoverningBody)}
                className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-zero-500 focus:outline-none focus:ring-2 focus:ring-zero-500/20"
              >
                {governingBodies.map((b) => (
                  <option key={b.value} value={b.value}>
                    {b.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                htmlFor="lookup-idtype"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                ID Type
              </label>
              <select
                id="lookup-idtype"
                value={idType}
                onChange={(e) => setIdType(e.target.value as IdType)}
                className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-zero-500 focus:outline-none focus:ring-2 focus:ring-zero-500/20"
              >
                {idTypes.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* ID Number (quick lookup) */}
          <div>
            <label htmlFor="lookup-id" className="mb-1 block text-sm font-medium text-gray-700">
              ID Number
            </label>
            <input
              id="lookup-id"
              type="text"
              value={idNumber}
              onChange={(e) => setIdNumber(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Enter ID number (auto-detects governing body)"
              className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm placeholder:text-gray-400 focus:border-zero-500 focus:outline-none focus:ring-2 focus:ring-zero-500/20"
            />
          </div>

          {/* Name search */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label
                htmlFor="lookup-firstname"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                First Name
              </label>
              <input
                id="lookup-firstname"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="First name"
                className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm placeholder:text-gray-400 focus:border-zero-500 focus:outline-none focus:ring-2 focus:ring-zero-500/20"
              />
            </div>
            <div>
              <label
                htmlFor="lookup-lastname"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                Last Name
              </label>
              <input
                id="lookup-lastname"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Last name"
                className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm placeholder:text-gray-400 focus:border-zero-500 focus:outline-none focus:ring-2 focus:ring-zero-500/20"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <Button onClick={handleSearch} loading={loading} disabled={loading}>
              <Search className="mr-1.5 h-4 w-4" />
              Search
            </Button>
            <Button variant="outline" onClick={handleClear}>
              Clear
            </Button>
          </div>
        </div>

        {/* Error */}
        {error && <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>}

        {/* Results */}
        {loading && (
          <div className="flex justify-center py-6">
            <div className="h-6 w-6 animate-spin rounded-full border-4 border-gray-200 border-t-zero-600" />
          </div>
        )}

        {!loading && hasSearched && results.length === 0 && (
          <div className="py-6 text-center">
            <User className="mx-auto h-10 w-10 text-gray-300" />
            <p className="mt-2 text-sm text-gray-500">No results found.</p>
            <p className="mt-1 text-xs text-gray-400">
              Try a different search term or check the governing body selection.
            </p>
          </div>
        )}

        {!loading && results.length > 0 && (
          <div>
            <p className="mb-3 text-xs font-medium uppercase tracking-wide text-gray-500">
              {results.length} result{results.length !== 1 ? 's' : ''} found
            </p>
            <div className="max-h-80 space-y-2 overflow-y-auto">
              {results.map((result) => (
                <Card key={result.id}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="truncate text-sm font-semibold text-gray-900">
                            {result.name}
                          </h4>
                          <Badge variant={bodyBadgeVariant[result.governingBody]}>
                            {result.governingBody}
                          </Badge>
                        </div>
                        <div className="mt-1 flex items-center gap-3 text-xs text-gray-500">
                          <span>
                            {result.idType}: <span className="font-mono">{result.idNumber}</span>
                          </span>
                          {result.classification && <span>Class: {result.classification}</span>}
                        </div>
                        {!result.isOnPlatform && (
                          <p className="mt-1 text-xs text-amber-600">
                            Not on Zero — exists in governing body records only
                          </p>
                        )}
                      </div>
                      <div className="flex-shrink-0">
                        {result.isOnPlatform && result.profileId ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleViewProfile(result.profileId!)}
                          >
                            <ExternalLink className="mr-1 h-3.5 w-3.5" />
                            View Profile
                          </Button>
                        ) : (
                          <span className="text-xs text-gray-400">Not on Zero</span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}
